import { useMemo } from 'react';

import {
  CoreDocumentsState,
  DocumentsSections,
  coreDocumentsDefaultState,
  deleteDocument,
  fetchDocument,
  loadDocuments,
  resetDocuments,
  saveDocument,
  uploadDocumentAttachment,
} from '@cd3p/core/modules/documents';
import { coreHelpCenterSendSupportRequest } from '@cd3p/core/modules/helpCenter';

import {
  bindActionCreators,
  simplifyBuilder,
  useDispatch,
  useSelector,
} from 'third-party';

import { appSelectors, helpCenterSelectors } from 'selectors';
import { APILanguage } from '@cd3p/core/types/api';

export type HelpCenterState = CoreDocumentsState & {
  loadHelpCenterDocumentsPending: boolean;
  fetchHelpCenterDocumentPending: boolean;
};

const helpCenterDefaultState: HelpCenterState = {
  loadHelpCenterDocumentsPending: false,
  fetchHelpCenterDocumentPending: false,
  ...coreDocumentsDefaultState,
};

export const DEFAULT_HELP_CENTER_SEARCH_COLUMNS = [
  'optimizedSearchText',
  'title',
];

const builder = simplifyBuilder(helpCenterDefaultState, {});

const sendSupportRequest = builder.createServerAction(
  coreHelpCenterSendSupportRequest,
);

export const loadHelpCenterDocuments = builder.createServerAction(
  loadDocuments('loadHelpCenterDocuments'),
);

export const fetchHelpCenterDocument = builder.createServerAction(
  fetchDocument('fetchHelpCenterDocument'),
);

export const saveHelpCenterDocument = builder.createServerAction(
  saveDocument('saveHelpCenterDocument'),
);

export const deleteHelpCenterDocument = builder.createServerAction(
  deleteDocument('deleteHelpCenterDocument'),
);

const uploadHelpCenterDocumentAttachment = builder.createServerAction(
  uploadDocumentAttachment('uploadHelpCenterDocumentAttachment'),
);

export const resetHelpCenterDocuments = builder.createReduxAction(
  resetDocuments('resetHelpCenterDocuments'),
);

export const useHelpCenter = (
  sectionId = DocumentsSections.HelpCenterWebDispatcher,
  language = APILanguage.English,
) => {
  const dispatch = useDispatch();
  const providerId = useSelector(appSelectors.providerId);

  return useMemo(
    () =>
      bindActionCreators(
        {
          sendSupportRequest: sendSupportRequest.bind(null, providerId),
          uploadHelpCenterDocumentAttachment:
            uploadHelpCenterDocumentAttachment.bind(
              null,
              providerId,
              sectionId,
            ),
          fetchHelpCenterDocument: fetchHelpCenterDocument.bind(
            null,
            providerId,
            sectionId,
          ),
          saveHelpCenterDocument: saveHelpCenterDocument.bind(
            null,
            providerId,
            sectionId,
            language,
          ),
          deleteHelpCenterDocument: deleteHelpCenterDocument.bind(
            null,
            providerId,
            sectionId || '',
          ),
          loadHelpCenterDocuments: loadHelpCenterDocuments.bind(
            null,
            providerId,
            sectionId,
            language,
            helpCenterSelectors.helpCenter,
          ),
          resetHelpCenterDocuments: resetHelpCenterDocuments.bind(null),
        },
        dispatch,
      ),
    [dispatch, providerId, sectionId, language],
  );
};

export const helpCenterReducer = builder.getReducers();
